import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase/firebase";
import { doc, getDoc, deleteDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { deleteUser, signOut, updatePassword } from "firebase/auth";
import "../styles/UserInfo.css";

function UserInfo() {
  const [userData, setUserData] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  //const [showPasswords, setShowPasswords] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setUserData({
          name: userSnap.data().name || user.displayName || "User",
          email: user.email,
          createdAt: user.metadata.creationTime,
        });
      }
    };
    fetchUserData();
  }, [navigate]);

  const handleDeleteAccount = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setDeleteError("No user logged in");
        return;
      }

      // Delete user data from Firestore
      await deleteDoc(doc(db, "users", user.uid));

      // Delete user authentication
      await deleteUser(user);

      // Sign out and redirect to login
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error deleting account:", error);
      if (error.code === "auth/requires-recent-login") {
        setDeleteError(
          "Please log out and log in again to delete your account for security reasons."
        );
      } else {
        setDeleteError("Error deleting account. Please try again.");
      }
    }
  };

  const handlePasswordChange = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setPasswordError("No user logged in");
        return;
      }

      if (newPassword.length < 6) {
        setPasswordError("Password should be at least 6 characters long");
        return;
      }

      if (newPassword !== confirmPassword) {
        setPasswordError("Passwords do not match");
        return;
      }

      await updatePassword(user, newPassword);
      setPasswordSuccess(true);
      setPasswordError("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        setShowPasswordDialog(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (error) {
      console.error("Error updating password:", error);
      if (error.code === "auth/requires-recent-login") {
        setPasswordError(
          "Please log out and log in again to change your password for security reasons."
        );
      } else {
        setPasswordError("Error updating password. Please try again.");
      }
    }
  };

  if (!userData) {
    return <div style={{ padding: 32 }}>Loading user info...</div>;
  }

  return (
    <div className="userinfo-outer">
      <div className="userinfo-card">
        <h2>User Information</h2>
        <div style={{ margin: "1.5rem 0" }}>
          <strong>Name:</strong>
          <div className="userinfo-field">{userData.name}</div>
          <strong>Email:</strong>
          <div className="userinfo-field">{userData.email}</div>
          <strong>Account Created:</strong>
          <div className="userinfo-field">{userData.createdAt}</div>
        </div>
        <div className="buttons-container">
          <button className="btn btn-back" onClick={() => navigate(-1)}>
            Back
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowPasswordDialog(true)}
          >
            Change Password
          </button>
          <button
            className="btn btn-danger"
            onClick={() => setShowConfirmDialog(true)}
          >
            Delete Account
          </button>
        </div>

        {deleteError && (
          <p style={{ color: "#dc3545", marginTop: "1rem" }}>{deleteError}</p>
        )}

        {showConfirmDialog && (
          <div className="modal-overlay">
            <div
              className="modal"
              style={{
                padding: "2rem",
                textAlign: "center",
              }}
            >
              <h3>Are you sure you want to delete your account?</h3>
              <p>This action cannot be undone.</p>
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "center",
                  marginTop: "1.5rem",
                }}
              >
                <button
                  onClick={handleDeleteAccount}
                  style={{
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    padding: "0.5rem 1rem",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Yes, Delete Account
                </button>
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  style={{
                    backgroundColor: "#6c757d",
                    color: "white",
                    border: "none",
                    padding: "0.5rem 1rem",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add the password change modal */}
        {showPasswordDialog && (
          <div className="modal-overlay">
            <div className="password-modal">
              <h3>Change Password</h3>
              <div className="password-form">
                <div className="password-input-group">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="password-input"
                  />
                  <button
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="password-toggle-btn"
                    type="button"
                    aria-label="Toggle password visibility"
                  >
                    {showNewPassword ? "👁️" : "👁️‍🗨️"}
                  </button>
                </div>

                <div className="password-input-group">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="password-input"
                  />
                  <button
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="password-toggle-btn"
                    type="button"
                    aria-label="Toggle password visibility"
                  >
                    {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                  </button>
                </div>

                {passwordError && (
                  <div className="password-feedback password-error">
                    {passwordError}
                  </div>
                )}
                {passwordSuccess && (
                  <div className="password-feedback password-success">
                    Password updated successfully!
                  </div>
                )}

                <div className="modal-buttons">
                  <button
                    onClick={handlePasswordChange}
                    className="modal-btn modal-btn-primary"
                  >
                    Update Password
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordDialog(false);
                      setNewPassword("");
                      setConfirmPassword("");
                      setPasswordError("");
                      setShowNewPassword(false);
                      setShowConfirmPassword(false);
                    }}
                    className="modal-btn modal-btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserInfo;
